import { Alignment, Fit, Layout } from '@rive-app/react-canvas';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { PauseIcon, PlayIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { AnnotationsV2, type AnnotationsV2Props } from '~/components/AnnotationsV2/AnnotationsV2';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { FileVisualizer } from '~/components/AssetModalV2/Visualizer/components/FileVisualizer';
import { PLAY_PAUSE_BUTTON, RIVE_VISUALIZER } from '~/constants/testIDs';
import { useRiveWithStateMachines } from '~/hooks/useRiveWithStateMachines';

export type RiveVisualizerInnerProps = {
  src: string;
  canAnnotateClip?: boolean;
} & Pick<AnnotationsV2Props, 'AnnotationsAvatars' | 'AnnotationComment' | 'FloatingCommentForm'>;

export const RiveVisualizerInner = memo(
  ({
    src,
    canAnnotateClip = false,
    AnnotationsAvatars,
    AnnotationComment,
    FloatingCommentForm,
  }: RiveVisualizerInnerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const riveContainerRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);

    // Get annotation context
    const isAnnotationsEnabled = useAnnotationV2Store((state) => state.isAnnotationsEnabled);
    const { isAboveMediumScreen } = useBreakpointsContext();

    // Calculate annotation permissions
    const canStartAnnotation = canAnnotateClip && isAboveMediumScreen;
    const canDrawAnnotation = canStartAnnotation && isAnnotationsEnabled;

    // Measure the Rive container dimensions
    const { height: containerHeight, width: containerWidth } = useResizeDetector<HTMLDivElement>({
      handleHeight: true,
      targetRef: riveContainerRef,
    });

    // Create layout inside component to avoid SSR issues
    const riveLayout = useMemo(
      () =>
        new Layout({
          fit: Fit.Contain,
          alignment: Alignment.Center,
        }),
      [],
    );

    // Use the custom hook for Rive with state machine detection
    const { RiveComponent, rive, setContainerRef, hasError } = useRiveWithStateMachines({
      src,
      autoplay: true,
      layout: riveLayout,
    });

    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React when the container div below mounts; Biome only sees the initial `null`.
      if (containerRef.current) {
        setContainerRef(containerRef.current);
      }
    }, [setContainerRef]);

    const handlePlayPause = useCallback(() => {
      if (!rive) {
        return;
      }

      if (isPlaying) {
        rive.pause();
        setIsPlaying(false);
      } else {
        rive.play();
        setIsPlaying(true);
      }
    }, [rive, isPlaying]);

    if (hasError) {
      // Show file visualizer for incompatible Rive files
      return <FileVisualizer />;
    }

    return (
      <div
        data-testid={RIVE_VISUALIZER}
        className="group/visualizer relative flex size-full max-h-full items-center justify-center"
      >
        <div ref={riveContainerRef} className="relative size-full">
          <div ref={containerRef} className="size-full">
            <RiveComponent />
          </div>

          {/* Annotations overlay */}
          {canAnnotateClip && !!containerWidth && !!containerHeight && (
            <div className="absolute inset-0 overflow-hidden">
              <AnnotationsV2
                AnnotationsAvatars={AnnotationsAvatars}
                AnnotationComment={AnnotationComment}
                FloatingCommentForm={FloatingCommentForm}
                pageNumber={-1}
                canDrawAnnotation={canDrawAnnotation}
                keyShortcutsEnabled={canStartAnnotation}
                width={containerWidth}
                height={containerHeight}
              />
            </div>
          )}
        </div>

        {/* Control overlay - appears on hover */}
        <div className="absolute bottom-4 left-4 z-20 flex gap-2 opacity-0 transition-opacity duration-200 group-hover/visualizer:opacity-100">
          <IconButton
            adsIcon
            appearance="filled"
            color="gray"
            data-testid={PLAY_PAUSE_BUTTON}
            onClick={handlePlayPause}
            label={isPlaying ? 'Pause animation' : 'Play animation'}
            icon={isPlaying ? PauseIcon : PlayIcon}
          />
        </div>
      </div>
    );
  },
);

RiveVisualizerInner.displayName = 'RiveVisualizerInner';
